/**
 * useSeasonRecap — decide quando o cartão de FECHAMENTO da temporada abre.
 *
 * O gatilho é do SERVIDOR: o boundary grava `users.seasonRecapPending =
 * seasonId` (CF-only — league-season-goals.md §1.5), porque o cartão carrega
 * prêmio real (o código de presente, a posição final); se o client pudesse
 * desmarcar, um bug de UI esconderia o próprio prêmio. O ACK é local
 * (`localStorage` por uid+temporada — decisão de implementação, mesma do
 * `useLeagueWeekRecap`): a flag do servidor faz o cartão abrir na 1ª sessão
 * de QUALQUER aparelho, e cada aparelho o dispensa uma vez.
 *
 * Mesmas travas do recap semanal: allowlist de rota, `celebrationGate` e
 * 600 ms de assentamento. Uma leitura (`season_standings/{id}/entries/{uid}`)
 * por uid enquanto a flag existir — e nenhuma quando não existe.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSeasonStanding, type SeasonStanding } from "../../../services/firebase/leagueService";
import { isCelebrationBlocked, subscribeCelebrationGate } from "../../gamification/utils/celebrationGate";
import { isSafeCelebrationRoute } from "../../gamification/hooks/useBadgeUnlockQueue";
import type { SeasonRecap } from "../components/season/SeasonRecapModal";

const SETTLE_MS = 600;
const storageKey = (uid: string) => `livra_season_recap_seen_${uid}`;

export function hasSeenSeasonRecap(uid: string, seasonId: string): boolean {
  try {
    return window.localStorage.getItem(storageKey(uid)) === seasonId;
  } catch {
    return false;
  }
}

export function markSeasonRecapSeen(uid: string, seasonId: string): void {
  try {
    window.localStorage.setItem(storageKey(uid), seasonId);
  } catch {
    /* storage indisponível: o cartão pode voltar nesta sessão, nunca perde o prêmio */
  }
}

export interface UseSeasonRecapResult {
  recap: SeasonRecap | null;
  dismiss: () => void;
}

export function useSeasonRecap(
  uid: string | null | undefined,
  pendingSeasonId: string | null | undefined,
): UseSeasonRecapResult {
  const { pathname } = useLocation();
  // O acumulador INTEIRO, não só o `final`: o card "Fim de temporada" desenha
  // a trilha e os totais (páginas, foco) que moram fora dele.
  const [loaded, setLoaded] = useState<{ key: string; standing: SeasonStanding | null } | null>(null);
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  const [open, setOpen] = useState<SeasonRecap | null>(null);
  const requestedRef = useRef<string | null>(null);

  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  const key = uid && pendingSeasonId ? `${uid}:${pendingSeasonId}` : null;

  useEffect(() => {
    if (!key || !uid || !pendingSeasonId) {
      requestedRef.current = null;
      return;
    }
    if (hasSeenSeasonRecap(uid, pendingSeasonId)) return;
    if (requestedRef.current === key) return;
    requestedRef.current = key;
    void (async () => {
      try {
        const standing = await getSeasonStanding(pendingSeasonId, uid);
        setLoaded({ key, standing });
      } catch (err) {
        requestedRef.current = null;
        console.error("[Lura] Falha ao carregar o fechamento da temporada:", err);
      }
    })();
  }, [key, uid, pendingSeasonId]);

  const standing = loaded && key && loaded.key === key ? loaded.standing : null;
  const final = standing?.final ?? null;
  const canOpen =
    !!final &&
    !!uid &&
    !!pendingSeasonId &&
    !blocked &&
    isSafeCelebrationRoute(pathname) &&
    !hasSeenSeasonRecap(uid, pendingSeasonId);

  useEffect(() => {
    if (open) return;
    if (!canOpen || !final || !pendingSeasonId) return;
    const t = setTimeout(() => setOpen({ seasonId: pendingSeasonId, final, standing }), SETTLE_MS);
    return () => clearTimeout(t);
  }, [canOpen, final, standing, pendingSeasonId, open]);

  const dismiss = () => {
    if (uid && open) markSeasonRecapSeen(uid, open.seasonId);
    setOpen(null);
  };

  return { recap: open, dismiss };
}
